import {
  Container, Row, Card
} from "react-bootstrap";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Page as Paging,
  PageSettingsModel,
  Filter,
  Resize,
  FilterSettingsModel
} from "@syncfusion/ej2-react-grids";
import {
  Page,
  Spinner,

  Divider
} from "@whitespace/whitespace-ui";
import { useEffect, useState, useCallback } from "react";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { parseUsingParseInt } from "helpers/stringUtils";
import "styles/sf-datagrid.scss";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import IContainerStatus from "interfaces/IContainerStatus";
import IContainerStatusViewModel from "interfaces/IContainerStatusViewModel";
import SiteAndRegionTemplate from "../Templates/SiteAndRegionTemplate";

import "./ContainerStatuses.scss";
import { mapContainerStatusesToContainerViewStatusModels } from "./DataMappers/ContainerStatusesMapper";
import ContainerTemplate from "../Templates/ContainerTemplate";
import LastUpdatedTemplate from "../Templates/LastUpdatedTemplate";
import FillLevelTemplate from "../Templates/FillLevelTemplate";
import WorkOrderTemplate from "../Templates/WorkOrderTemplate";

const pageSize: number = parseUsingParseInt(process.env.REACT_APP_PAGE_SIZE);
const pageSettings: PageSettingsModel = {
  pageSize,
  pageSizes: [5, 10, 20, 50, "All"]
};
const filterSettings: FilterSettingsModel = { type: "Excel" };
let grid: GridComponent | undefined;

const ContainerStatuses = () => {
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [renderingComplete, setRenderingComplete] = useState(false);

  const {
    isSending, errors, data, statusCode, sendRequest
  } = useAmplifyREST();
  const [containerViewModels, setContainerViewModels] = useState<IContainerStatusViewModel[] | undefined>(undefined);

  useEffect(() => {
    sendRequest("container/statuses", "GET");
  }, [sendRequest]);

  useEffect(() => {
    if (data && data.containers) {
      const containerStatuses = data.containers as IContainerStatus[];
      const mappings = mapContainerStatusesToContainerViewStatusModels(containerStatuses!);
      setContainerViewModels(mappings);
    }
  }, [data, setContainerViewModels]);

  useEffect(() => {
    if (![0, 200].includes(statusCode)) {
      setRenderingComplete(true);
    }
  }, [statusCode]);

  useEffect(() => {
    if (errors) setShowExceptions(true);
  }, [errors]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const siteAndRegionTemplateInstance = SiteAndRegionTemplate;
  const containerTemplateInstance = ContainerTemplate;
  const lastUpdatedTemplateInstance = LastUpdatedTemplate;
  const fillLevelTemplateInstance = FillLevelTemplate;
  const workOrderTemplateInstance = WorkOrderTemplate;

  return (
    <Page>
      {showExceptions && errors && (
        <AmplifyError
          errors={errors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="region-management">
              Container Status
              <Spinner
                animation="grow"
                visible={isSending || !renderingComplete}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          <section className="regions pt-2">
            <Container fluid>
              <Row>
                <div className="control-pane">
                  <div className="control-section">
                    <GridComponent
                      ref={(g: GridComponent) => {
                        grid = g;
                      }}
                      id="overviewgrid"
                      data-testid="gridContainerStatuses"
                      allowPaging
                      allowResizing
                      pageSettings={pageSettings}
                      allowSorting
                      allowFiltering
                      filterSettings={filterSettings}
                      allowSelection={false}
                      dataSource={containerViewModels}
                      actionComplete={() => {
                        makeGridAACompliant(grid!, () => {
                          setRenderingComplete(true);
                        });
                      }}
                      loadingIndicator={{ indicatorType: "Shimmer" }}
                      type=""
                    >
                      <ColumnsDirective>
                        <ColumnDirective
                          field="id"
                          headerText="id"
                          visible={false}
                          isPrimaryKey
                        />
                        <ColumnDirective
                          field="regionHwrcSiteSortOrder"
                          headerText="Site and Region"
                          template={siteAndRegionTemplateInstance}
                          width="26%"
                        />
                        <ColumnDirective
                          field="wasteType"
                          headerText="Container"
                          template={containerTemplateInstance}
                          width="26%"
                        />
                        <ColumnDirective
                          field="lastUpdatedTimestamp"
                          headerText="Updated"
                          template={lastUpdatedTemplateInstance}
                          width="16%"
                        />
                        <ColumnDirective
                          field="fillLevel"
                          headerText="Fill Level"
                          template={fillLevelTemplateInstance}
                          width="15%"
                        />
                        <ColumnDirective
                          field="workOrderReferenceNumber"
                          data-testid="workOrderReferenceNumber"
                          headerText="Work Order"
                          headerTextAlign="Center"
                          template={workOrderTemplateInstance}
                          width="15%"
                        />
                      </ColumnsDirective>
                      <Inject services={[Filter, Paging, Sort, Resize]} />
                    </GridComponent>
                  </div>
                </div>
              </Row>
            </Container>
          </section>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default ContainerStatuses;
